/**
 * THE SOLIDS OVER A GROUND POINT, by a uniform grid (the perf overhaul, Stage 1).
 *
 * The reach flood asks, for every tile it visits, what surface stands there and what the going is - and both
 * answers were found by asking EVERY solid on the map. On a big scene that scan was most of a drag's stall. Here
 * each solid is filed once under every CELL_M cell its ground bounds touch, so a point asks only the few filed
 * under its own cell.
 *
 * `at(x, z)` returns a SUPERSET: every solid whose bounds hold the point, and maybe a neighbour whose bounds only
 * share the cell - the callers' own bounds test (surfaceOf, containsPoint) is still the answer. Order is the list's,
 * so nothing that reads the first match changes. Built once per list of solids (lists are never mutated in place:
 * a solid edit makes a new list); pure.
 */
import { solidBounds } from './solids';
import type { Solid } from './types';

/** Cell edge, metres: about a tile, so a cell holds a handful of solids even on a dense map. */
const CELL_M = 2;
const NONE: readonly Solid[] = [];

export interface SolidIndex {
  /** The solids filed under the cell of (x, z): those whose bounds hold it, maybe a few more. */
  at(x: number, z: number): readonly Solid[];
  /**
   * The solids filed under every cell the ground segment (x0, z0)-(x1, z1) passes, each once: every solid whose
   * ground bounds it crosses, maybe a few more (the GPU map's sight lines, perf overhaul 2b).
   */
  alongSegment(x0: number, z0: number, x1: number, z1: number): readonly Solid[];
}

const cell = (v: number) => Math.floor(v / CELL_M);
// A number, not a string: a sight line's walk asks for thousands of cells a frame (perf overhaul 2b). Exact for any
// cell index within a million of the origin, which any arena is.
const keyOf = (cx: number, cz: number) => cx * 2_097_152 + cz;

function file(cells: Map<number, Solid[]>, solid: Solid) {
  const { min, max } = solidBounds(solid);
  for (let cx = cell(min.x); cx <= cell(max.x); cx++) {
    for (let cz = cell(min.z); cz <= cell(max.z); cz++) {
      const k = keyOf(cx, cz);
      const here = cells.get(k);
      if (here) here.push(solid);
      else cells.set(k, [solid]);
    }
  }
}

/** One axis of the cell walk: the step's sign, the distance (as a share of the segment) to the next line, per cell. */
function axisStep(from: number, d: number, c: number) {
  if (d === 0) return { step: 0, next: Infinity, each: Infinity };
  const step = Math.sign(d);
  const edge = step > 0 ? (c + 1) * CELL_M - from : from - c * CELL_M;
  return { step, next: edge / Math.abs(d), each: CELL_M / Math.abs(d) };
}

/**
 * Every cell the segment passes, in order (Amanatides and Woo's walk), handed to `visit`. An axis that has reached
 * its end cell steps no further: float drift in `next` on a near-tie can then only swap the order of two steps,
 * never end the walk a cell off.
 */
function walkCells(x0: number, z0: number, x1: number, z1: number, visit: (cx: number, cz: number) => void) {
  const x = { ...axisStep(x0, x1 - x0, cell(x0)), at: cell(x0), end: cell(x1) };
  const z = { ...axisStep(z0, z1 - z0, cell(z0)), at: cell(z0), end: cell(z1) };
  visit(x.at, z.at);
  while (x.at !== x.end || z.at !== z.end) {
    const axis = z.at === z.end || (x.at !== x.end && x.next < z.next) ? x : z;
    axis.at += axis.step;
    axis.next += axis.each;
    visit(x.at, z.at);
  }
}

const built = new WeakMap<readonly Solid[], SolidIndex>();

/** The index of `solids`, built on first ask. */
export function solidIndex(solids: readonly Solid[]): SolidIndex {
  const hit = built.get(solids);
  if (hit) return hit;
  const cells = new Map<number, Solid[]>();
  for (const solid of solids) file(cells, solid);
  const alongSegment = (x0: number, z0: number, x1: number, z1: number) => {
    const found = new Set<Solid>();
    walkCells(x0, z0, x1, z1, (cx, cz) => {
      for (const s of cells.get(keyOf(cx, cz)) ?? NONE) found.add(s);
    });
    return [...found];
  };
  const index: SolidIndex = { at: (x, z) => cells.get(keyOf(cell(x), cell(z))) ?? NONE, alongSegment };
  built.set(solids, index);
  return index;
}
